import { useRegistrationPurposes, useRegistrations } from '@commenda/api';
import {
  Badge,
  Button,
  DataTable,
  Dropdown,
  FlagComponent,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { RegistrationApplicabilityFilter } from '@commenda/types/src/registration/types';
import { NonTimezoneSensitiveDate, RegistrationHelper } from '@commenda/utils';
import { useState } from 'react';
import { useJurisdictionLookup } from '../../../../hooks/useJurisdictionLookup';
import { AddFQRegistrationModal } from './Modals/AddFQRegistrationModal';
import { DeleteRegistrationModal } from './Modals/DeleteRegistrationModal';

export const ForeignQualificationRegistration = ({
  businessEntity,
  hideAddButton = false,
  hideActions = false,
}: {
  businessEntity: IBusinessEntity;
  hideAddButton?: boolean;
  hideActions?: boolean;
}) => {
  const { placeOf } = useJurisdictionLookup();
  const addForeignQualificationRegistrationModalVisibility = useToggle();

  const deleteRegistrationModalVisibility = useToggle();

  // The row the add/edit form is bound to, kept apart from the row a delete is
  // acting on: a cancelled delete must not leave the next add in edit mode.
  const [editingRegistrationId, setEditingRegistrationId] = useState<
    string | null
  >(null);
  const [actioningRegistrationId, setActioningRegistrationId] = useState<
    string | null
  >(null);
  const { data: registrationsResponse, mutate: refetchRegistrations } =
    useRegistrations(
      businessEntity.id,
      undefined,
      RegistrationApplicabilityFilter.FOREIGN
    );

  const { purposeLabels } = useRegistrationPurposes();

  const foreignQualificationRegistrations =
    registrationsResponse?.registrations || [];

  return (
    <div className="flex-1 h-full overflow-y-auto flex flex-col gap-3xl">
      <AddFQRegistrationModal
        companyId={businessEntity.generalCorporationInfo?.companyId}
        isOpen={addForeignQualificationRegistrationModalVisibility.visible}
        onClose={() => {
          setEditingRegistrationId(null);
          addForeignQualificationRegistrationModalVisibility.off();
        }}
        onSuccess={async () => {
          await refetchRegistrations();
          addForeignQualificationRegistrationModalVisibility.off();
          setEditingRegistrationId(null);
        }}
        businessEntity={{
          country: businessEntity.generalCorporationInfo.incorporationCountry,
          id: businessEntity.id,
        }}
        mode={editingRegistrationId ? 'edit' : 'create'}
        registrationId={editingRegistrationId}
      />
      <DeleteRegistrationModal
        isOpen={deleteRegistrationModalVisibility.state}
        onClose={() => {
          setActioningRegistrationId(null);
          deleteRegistrationModalVisibility.off();
        }}
        registrationId={actioningRegistrationId}
        businessEntityId={businessEntity.id}
        registrationName={
          foreignQualificationRegistrations.find(
            (registration) => registration.id === actioningRegistrationId
          )?.displayName
        }
        onSuccess={async () => {
          setActioningRegistrationId(null);
          await refetchRegistrations();
          deleteRegistrationModalVisibility.off();
        }}
      />
      <div className="flex justify-between items-center">
        <h2 className="display-small-semibold">
          Foreign Qualification Registration
        </h2>

        {!hideAddButton && (
          <Button
            size="sm"
            onClick={() => {
              setEditingRegistrationId(null);
              addForeignQualificationRegistrationModalVisibility.show();
            }}
          >
            <Icons.Plus className="size-4" />
            Add state registration
          </Button>
        )}
      </div>

      <DataTable
        columnGroups={[
          {
            id: 'foreign-qualification-registrations',
            columns: [
              {
                id: 'displayName',
                accessorKey: 'displayName',
                header: 'Registration name',
                title: 'Registration name',
                cell: ({ row }) => {
                  return (
                    <div className="py-md">
                      <span className="txt-small-medium">
                        {row.original.displayName}
                      </span>
                    </div>
                  );
                },
              },
              {
                id: 'registrationNumber',
                accessorKey: 'registrationNumber',
                header: 'Registration number',
                title: 'Registration number',
                cell: ({ row }) => {
                  return (
                    <div className="py-md">
                      <span className="txt-small-medium">
                        {(row.original.governmentIdentifiers ?? [])
                          .map((identifier) => identifier.value)
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    </div>
                  );
                },
              },
              {
                id: 'purpose',
                accessorKey: 'purpose',
                header: 'Purpose',
                title: 'Purpose',
                cell: ({ row }) => {
                  return (
                    <Badge
                      variant={RegistrationHelper.getRegistrationPurposeBadgeVariant(
                        row.original.purpose
                      )}
                      size="sm"
                    >
                      {purposeLabels.get(row.original.purpose)}
                    </Badge>
                  );
                },
              },
              {
                id: 'jurisdiction',
                accessorKey: 'jurisdiction',
                header: 'Jurisdiction',
                title: 'Jurisdiction',
                cell: ({ row }) => {
                  return (
                    <div className="flex items-center gap-xs">
                      <FlagComponent
                        country={RegistrationHelper.getRegistrationCountry(
                          row.original.country
                        )}
                        countryName={RegistrationHelper.getRegistrationCountry(
                          row.original.country
                        )}
                      />
                      <span>
                        {placeOf(
                          row.original.jurisdictionId,
                          row.original.country
                        )}
                      </span>
                    </div>
                  );
                },
              },
              {
                id: 'status',
                accessorKey: 'status',
                header: 'Status',
                title: 'Status',
                cell: ({ row }) => {
                  return (
                    <Badge
                      variant={RegistrationHelper.getRegistrationStatusBadgeVariant(
                        row.original.status
                      )}
                      size="sm"
                    >
                      {
                        RegistrationHelper.REGISTRATION_VALIDATION_STATUS_MAP[
                          row.original.status
                        ]
                      }
                    </Badge>
                  );
                },
              },
              {
                id: 'effectiveEndDate',
                accessorKey: 'effectiveEndDate',
                header: 'Expiration date',
                title: 'Expiration date',
                cell: ({ row }) => {
                  return (
                    <span>
                      {row.original.effectiveEndDate
                        ? NonTimezoneSensitiveDate.fromISO8601String(
                            row.original.effectiveEndDate
                          ).format('Do MMM YYYY')
                        : '-'}
                    </span>
                  );
                },
              },
              {
                id: 'actions',
                accessorKey: 'actions',
                header: '',
                title: '',
                cell: ({ row }) => {
                  if (hideActions) return null;
                  return (
                    <div className="w-full flex justify-end">
                      <Dropdown
                        menu={{
                          groups: [
                            {
                              items: [
                                {
                                  label: 'Edit details',
                                  icon: Icons.Edit02,
                                  onClick: () => {
                                    setEditingRegistrationId(row.original.id);
                                    addForeignQualificationRegistrationModalVisibility.show();
                                  },
                                },
                                {
                                  label: 'Delete',
                                  icon: Icons.Trash01,
                                  onClick: () => {
                                    setActioningRegistrationId(row.original.id);
                                    deleteRegistrationModalVisibility.show();
                                  },
                                  labelClassName: 'text-utility-error-600',
                                  iconClassName: 'text-utility-error-600',
                                },
                              ],
                            },
                          ],
                        }}
                        align="end"
                      >
                        <div className="size-8 flex justify-center items-center group -translate-x-4">
                          <Icons.DotsVertical className="size-4 text-fg-quaternary group-hover:text-fg-quaternary-hover" />
                        </div>
                      </Dropdown>
                    </div>
                  );
                },
              },
            ],
          },
        ]}
        data={foreignQualificationRegistrations}
        rowDataAttributes={(row) => ({
          'data-ai-item': 'State registration',
          ...(row.id ? { 'data-ai-id': String(row.id) } : {}),
        })}
        emptyStateConfig={{
          title: 'No foreign qualification registrations found',
          description:
            'Add a foreign qualification registration to get started',
        }}
      />
    </div>
  );
};
